import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon, InstagramIcon, CalendarCheckIcon, FacebookIcon, YoutubeIcon } from 'lucide-react';
import { contact, navLinks } from '../data/site';
import { Logo } from './Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { useMotionPreferences } from '../hooks/useMotionPreferences';
import { fadeUp, staggerContainer, viewportOnce } from '../utils/motion';
export function Footer() {
  const {
    reduced
  } = useMotionPreferences();
  const {
    t
  } = useLanguage();
  const item = fadeUp(reduced, 22);
  return <footer id="contact" className="relative w-full overflow-hidden border-t border-white/10 bg-navy-deep pt-20" data-id="element-59" data-mp="3leviv7odbqit">
      <div className="pointer-events-none absolute left-1/2 -top-24 h-64 w-[40rem] -translate-x-1/2 rounded-full bg-grape/20 blur-[140px]" data-id="element-60" data-mp="3qc445h4tcaoa" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8" data-id="element-61" data-mp="2ey9zrxfcyroe">
        <motion.div variants={staggerContainer(0.09)} initial="hidden" whileInView="show" viewport={viewportOnce} className="grid gap-12 pb-14 lg:grid-cols-4" data-id="element-62" data-mp="2ezutxlxrg8lq">
          <motion.div variants={item} className="lg:col-span-2" data-id="element-63" data-mp="3gcjvsqtw40gw">
            <Logo className="h-14" data-id="element-64" data-mp="3v8hp81cfiqpf" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/55" data-id="element-65" data-mp="3v5dfkog732rd">
              {t.footer.tagline}
            </p>
            <div className="mt-7 flex items-center gap-3" data-id="element-66" data-mp="82idhvaq8efw">
              {[{
              Icon: InstagramIcon,
              href: contact.instagramHref,
              label: 'Instagram'
            }, {
              Icon: FacebookIcon,
              href: contact.instagramHref,
              label: 'Facebook'
            }, {
              Icon: YoutubeIcon,
              href: contact.instagramHref,
              label: 'YouTube'
            }].map(({
              Icon,
              href,
              label
            }) => <motion.a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} whileHover={reduced ? {
              opacity: 0.85
            } : {
              scale: 1.1,
              y: -3
            }} whileTap={reduced ? undefined : {
              scale: 0.94
            }} transition={{
              type: 'spring',
              stiffness: 420,
              damping: 24
            }} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-[box-shadow,border-color,color] duration-300 hover:border-magenta hover:text-magenta hover:shadow-glow-magenta" data-id="element-67" data-mp="qt8fziwdxz88">
                  <Icon className="h-5 w-5" data-id="element-68" data-mp="1hkp8qrsvadhg" />
                </motion.a>)}
            </div>
          </motion.div>

          <motion.nav variants={item} aria-label="Footer" data-id="element-69" data-mp="1mjihy814fibt">
            <h2 className="font-display text-xs font-extrabold uppercase tracking-[0.22em] text-white/45" data-id="element-70" data-mp="1mhxmmg6zfvcw">
              {t.footer.explore}
            </h2>
            <ul className="mt-5 space-y-3" data-id="element-71" data-mp="3ocggmkhkx54o">
              {navLinks.map((link, i) => <li key={link.href} data-id="element-72" data-mp="3tb9p1yi0e5xz" data-mpi={i}>
                  <a href={link.href} className="text-sm text-white/65 transition-colors hover:text-magenta" data-id="element-73" data-mp="3tccdlzruzwaq">
                    {t.nav.links[i]}
                  </a>
                </li>)}
            </ul>
          </motion.nav>

          <motion.div variants={item} data-id="element-74" data-mp="21kfni01g7lut">
            <h2 className="font-display text-xs font-extrabold uppercase tracking-[0.22em] text-white/45" data-id="element-75" data-mp="1woi75tq6qfi1">
              {t.footer.contact}
            </h2>
            <ul className="mt-5 space-y-4 text-sm text-white/65" data-id="element-76" data-mp="2tbn5sqky7blg">
              <li className="flex gap-3" data-id="element-77" data-mp="2pdpd3dryy7db">
                <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-electric" data-id="element-78" data-mp="2kdb7019xh7hx" />
                <span data-id="element-79" data-mp="2kfe6rd0vuezu">{contact.address}</span>
              </li>
              <li className="flex gap-3" data-id="element-80" data-mp="2fd5d0eg6r8wo">
                <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-electric" data-id="element-81" data-mp="2fgkqjcu7o9vz" />
                <a href={contact.phoneHref} className="transition-colors hover:text-magenta" data-id="element-82" data-mp="18zfqop7aesp4">
                  {contact.phone}
                </a>
              </li>
              <li className="flex gap-3" data-id="element-83" data-mp="2rvhjbwxt0rca">
                <InstagramIcon className="mt-0.5 h-4 w-4 shrink-0 text-electric" data-id="element-84" data-mp="2rtmtq5bl8b1v" />
                <a href={contact.instagramHref} target="_blank" rel="noreferrer" className="transition-colors hover:text-magenta" data-id="element-85" data-mp="2wo0smhpgl0ba">
                  {contact.instagram}
                </a>
              </li>
              <li className="flex gap-3" data-id="element-86" data-mp="3bk8b9dl7xtog">
                <CalendarCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-electric" data-id="element-87" data-mp="3flbvecaaubvh" />
                <a href={contact.bookingHref} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-magenta" data-id="element-88" data-mp="3fizc9vst1vgu">
                  {t.footer.booking}
                </a>
              </li>
            </ul>
          </motion.div>
        </motion.div>

        <div className="flex flex-col gap-3 border-t border-white/10 py-7 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between" data-id="element-89" data-mp="1v69wmllg8c6s">
          <p data-id="element-90" data-mp="1imuypl1yz73v">
            © {new Date().getFullYear()} Pulse Padel Hub. {t.footer.rights}
          </p>
          <p className="uppercase tracking-[0.18em]" data-id="element-91" data-mp="1wmdv5glf4u0c">{t.footer.motto}</p>
        </div>
      </div>
    </footer>;
}